import React from 'react'

import qs from '@scoir/query-string'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router-dom'

import { useEnableRefreshSortOnPrefsChange } from 'common/use-refresh-sort'
import { useScrollResetter } from 'common/use-scroll-resetter'
import { AuthShieldRoute } from 'components/AuthShield'
import { registerUnauthedRoute } from 'components/AuthShield'
import { FEATURE_NAMES } from 'components/ConfiguredDynamicFeatureProvider/features'
import CopyrightFooter from 'components/CopyrightFooter'
import { useIsDynamicFeatureEnabled } from 'components/DynamicFeatureProvider/use-is-dynamic-feature-enabled'
import FourOhFour from 'components/FourOhFour'
import { useIsTestUser } from 'store/user/hooks'

import RememberedRedirect from './RememberedRedirect'
import { routeRequiredActions } from './required-actions'
import {
  CareerRoute,
  ChangePasswordRoute,
  CoalitionAppBuilderRoute,
  CollegeCompareRoute,
  CollegeRoute,
  CommonAppConfirmLinkRoute,
  ConfirmClassLinkLinkRoute,
  ConfirmCleverLinkRoute,
  ConfirmGoogleLinkRoute,
  ConfirmGuidanceOrgLinkRoute,
  ConfirmLinkRoute,
  DashboardRoute,
  DiscoverRoute,
  EmailSuggestionRoute,
  EventsRoute,
  MessageCenterRoute,
  MobileDocCenterRoute,
  MobileFormDetailsRoute,
  MobileONetInterestProfilerRoute,
  MobilePrinciplesYouRoute,
  MyCollegesRoute,
  MyProfileRoute,
  RequiredActionsRoute,
  SurveysRoute,
} from './routes'
import DashboardSplashPage from './routes/Dashboard/SplashPage'
import InAppLayout from './routes/InAppLayout'
import MyCollegesSplashPage from './routes/MyColleges/SplashPage'
import HomeRoute from './routes/home/Home'
import MessageCenterSplashPage from './routes/message-center/SplashPage'

const ROOT_PATH = import.meta.env.BASE_URL
const collegeDetailsPath = registerUnauthedRoute(`${ROOT_PATH}/colleges/:scid/:section?`)
const path404 = registerUnauthedRoute(`${ROOT_PATH}/404`)
const dashboardPath = registerUnauthedRoute(`${ROOT_PATH}/dashboard`)
const myCollegesPath = registerUnauthedRoute(`${ROOT_PATH}/my-colleges`)
const messageCenterPath = registerUnauthedRoute(`${ROOT_PATH}/message-center`)
const comparePath = registerUnauthedRoute(`${ROOT_PATH}/college-compare`)
const collegeSearchPath = registerUnauthedRoute(`${ROOT_PATH}/discover/search`)
registerUnauthedRoute(`${ROOT_PATH}/colleges/:scid`) // This is needed to support links to the scid, where it then redirects to /overview
registerUnauthedRoute(`${collegeSearchPath}/careers`) // This is needed to support careers splash page
registerUnauthedRoute(`${messageCenterPath}/outreach`) // This is needed to support inbox splash page

export const AuthRoutes = (props) => {
  const isTestUser = useIsTestUser()
  const hasMyColleges = useIsDynamicFeatureEnabled(FEATURE_NAMES.MyColleges)
  const hasSurveys = useIsDynamicFeatureEnabled(FEATURE_NAMES.Surveys)
  const hasEventSearch = useIsDynamicFeatureEnabled(FEATURE_NAMES.EventSearch)

  useScrollResetter(get(props, 'location.pathname'))
  useEnableRefreshSortOnPrefsChange()

  return (
    <InAppLayout
      {...props}
      footer={
        <Switch>
          <Route
            path={`${ROOT_PATH}/dashboard`}
            component={null}
          />
          <Route
            path={`${ROOT_PATH}/app-profile`}
            exact={false}
            component={null}
          />
          <Route
            path={`${ROOT_PATH}/mobile/documents`}
            exact={false}
            component={null}
          />
          <Route
            path={myCollegesPath}
            component={null}
          />
          <Route path="/">
            <CopyrightFooter />
          </Route>
        </Switch>
      }
    >
      {isTestUser && (
        <Switch>
          <Route
            path={`${ROOT_PATH}/app-profile`}
            exact={false}
            component={CoalitionAppBuilderRoute}
          />
          <Route
            path="*"
            render={() => <Redirect to={`${ROOT_PATH}/app-profile`} />}
          />
        </Switch>
      )}
      {!isTestUser && (
        <Switch>
          <Redirect
            exact
            path={`${ROOT_PATH}/email/update-application-outcomes`}
            to={routeRequiredActions(['OUTCOMES'])}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/email/early-decision-signature`}
            to={routeRequiredActions(['EARLY-DECISION-CONTRACT'])}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/email/early-decision-contract`}
            to={`${ROOT_PATH}/my-colleges`}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/email/fee-waiver-signature`}
            to={routeRequiredActions(['FEE-WAIVER'])}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/email/fee-waiver`}
            to={`${ROOT_PATH}/my-colleges`}
          />

          <Route
            path={`${ROOT_PATH}/email/mydrive/:driveId`}
            render={(props) => {
              const match = props.match
              const params = match.params
              const driveId = params.driveId
              return <Redirect to={`${ROOT_PATH}/profile/drive?driveId=${driveId}`} />
            }}
          />
          <Route
            path={`${ROOT_PATH}/email/mydrive`}
            render={() => <Redirect to={`${ROOT_PATH}/profile/drive`} />}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/email/assignments`}
            to={`${ROOT_PATH}/dashboard`}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/college-search`}
            to={collegeSearchPath}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/colleges/search`}
            to={`${ROOT_PATH}/discover/for-you/my-matches`}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/colleges/search/advanced`}
            to={`${ROOT_PATH}/discover/search`}
          />
          <Redirect
            exact
            path={`${ROOT_PATH}/colleges/search/my-preferences`}
            to={`${ROOT_PATH}/discover/for-you/my-matches`}
          />
          {!hasEventSearch ? null : (
            <Route
              exact
              path={`${ROOT_PATH}/events`}
              render={(props) => <EventsRoute {...props} />}
            />
          )}
          {!hasSurveys ? null : (
            <Route
              path={`${ROOT_PATH}/surveys/:id?`}
              render={(props) => (
                <SurveysRoute
                  {...props}
                  selectedId={props.match.params.id}
                />
              )}
            />
          )}
          <Route
            path={comparePath}
            render={(props) => <CollegeCompareRoute {...props} />}
          />
          <Route
            path={collegeDetailsPath}
            render={(props) => {
              const match = props.match
              const params = match.params
              const scid = params.scid
              const searchParams = qs.parse(props.location.search)
              return (
                <CollegeRoute
                  {...props}
                  url={match.url}
                  searchParams={searchParams}
                  section={params.section}
                  scid={parseInt(scid, 10)}
                />
              )
            }}
          />
          <Route
            path={`${ROOT_PATH}/mobile/documents`}
            exact={false}
            component={MobileDocCenterRoute}
          />
          <Route
            path={`${ROOT_PATH}/mobile/principles-you-assessment`}
            exact={false}
            component={MobilePrinciplesYouRoute}
          />
          <Route
            path={`${ROOT_PATH}/mobile/career-interests-assessment`}
            exact={false}
            component={MobileONetInterestProfilerRoute}
          />
          <Route
            exact={false}
            path={`${ROOT_PATH}/mobile/form/:formId`}
            component={MobileFormDetailsRoute}
          />
          <Route
            path={`${ROOT_PATH}/careers/:soccode?`}
            render={(props) => {
              const match = props.match
              const params = match.params
              const searchParams = qs.parse(props.location.search)
              return (
                <CareerRoute
                  {...props}
                  url={match.url}
                  searchParams={searchParams}
                  soccode={params.soccode}
                />
              )
            }}
          />
          <AuthShieldRoute
            path={dashboardPath}
            authedComponent={DashboardRoute}
            unauthedComponent={DashboardSplashPage}
          />
          <Route
            path={`${ROOT_PATH}/discover`}
            render={(props) => <DiscoverRoute {...props} />}
          />
          <Route
            path={`${ROOT_PATH}/email/suggestion`}
            component={EmailSuggestionRoute}
          />
          <Route
            path={`${ROOT_PATH}/change-password`}
            render={(props) => <ChangePasswordRoute {...props} />}
          />
          <Route
            path={`${ROOT_PATH}/profile`}
            render={(props) => (
              <MyProfileRoute
                {...props}
                mountPath={props.match.path}
              />
            )}
          />
          <Route
            path={`${ROOT_PATH}/common-app/confirm`}
            render={(props) => <CommonAppConfirmLinkRoute {...props} />}
          />
          {!hasMyColleges ? null : (
            <Route
              path={`${ROOT_PATH}/app-profile`}
              exact={false}
              component={CoalitionAppBuilderRoute}
            />
          )}
          {!hasMyColleges ? null : (
            <AuthShieldRoute
              path={myCollegesPath}
              authedComponent={MyCollegesRoute}
              unauthedComponent={MyCollegesSplashPage}
            />
          )}
          {!hasMyColleges ? null : (
            <Route
              path={`${ROOT_PATH}/my-colleges`}
              render={(props) => <MyCollegesRoute {...props} />}
            />
          )}
          {!hasMyColleges ? null : (
            <AuthShieldRoute
              path={messageCenterPath}
              authedComponent={MessageCenterRoute}
              unauthedComponent={MessageCenterSplashPage}
            />
          )}
          <Route
            path={`${ROOT_PATH}/confirm/clever-link/:highSchoolId`}
            render={(props) => (
              <ConfirmLinkRoute
                {...props}
                highSchoolId={props.match.params.highSchoolId}
                confirmMethod="clever"
              />
            )}
          />
          <Route
            path={`${ROOT_PATH}/confirm/classlink-link/:highSchoolId`}
            render={(props) => (
              <ConfirmLinkRoute
                {...props}
                highSchoolId={props.match.params.highSchoolId}
                confirmMethod="classLink"
              />
            )}
          />
          <Route
            path={`${ROOT_PATH}/confirm/link/:highSchoolId/:linkCode`}
            render={(props) => (
              <ConfirmLinkRoute
                {...props}
                highSchoolId={props.match.params.highSchoolId}
                linkCode={props.match.params.linkCode}
              />
            )}
          />
          <Route
            path={`${ROOT_PATH}/confirm/guidance-org/:guidanceOrgId/link/:linkCode`}
            render={(props) => (
              <ConfirmGuidanceOrgLinkRoute
                {...props}
                guidanceOrgId={props.match.params.guidanceOrgId}
                linkCode={props.match.params.linkCode}
              />
            )}
          />
          <Route
            path={`${ROOT_PATH}/oauth/clever-link`}
            render={(props) => <ConfirmCleverLinkRoute {...props} />}
          />
          <Route
            path={`${ROOT_PATH}/oauth/classlink-link`}
            render={(props) => <ConfirmClassLinkLinkRoute {...props} />}
          />
          <Route
            path={`${ROOT_PATH}/oauth/google-link`}
            render={(props) => <ConfirmGoogleLinkRoute {...props} />}
          />
          <Route
            path={`${ROOT_PATH}/required-actions`}
            render={(props) => <RequiredActionsRoute mountedPath={props.match.path} />}
          />
          <Route
            path={`${ROOT_PATH}/home`}
            render={(props) => <HomeRoute {...props} />}
          />
          <Route
            path={`${ROOT_PATH}/404`}
            render={() => (
              <FourOhFour onGoHome={() => (window.location.href = `${window.location.origin}${ROOT_PATH}/home`)} />
            )}
          />
          <Redirect
            exact
            from={`${ROOT_PATH}/`}
            to={`${ROOT_PATH}/home`}
          />
          <Redirect to={path404} />
        </Switch>
      )}
      <RememberedRedirect />
    </InAppLayout>
  )
}

AuthRoutes.propTypes = {
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export default AuthRoutes
